import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { useStore, userActions } from '@modules';
import { VerifyNextEnum } from '@modules/types';

import Button from '@components/Button';
import Input from '@components/Input';
import LoginLayout from '@layouts/LoginLayout';

import style from './VerifyPassword.scss';

const cn = bemCssModules(style, 'VerifyPassword');
const ti = getTestId('VerifyPassword');

export const VerifyPassword: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation<LocationStateType['verifyPassword']>();

  const [{ user }, dispatch] = useStore();
  const { changeEmailVerifyPassword } = user.loaders;
  const isLoading = !!changeEmailVerifyPassword;
  const error = user.errors?.message;

  const [password, setPassword] = useState<string>('');

  useEffect(() => {
    dispatch(userActions.clearErrors());
  }, []);

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) dispatch(userActions.clearErrors());
    setPassword(e.target.value);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(userActions.changeEmailVerifyPassword({ password, token: location.state?.token }))
      .unwrap()
      .then(({ data }) => {
        if (data.next === VerifyNextEnum.sendNewEmail) {
          navigate<'sendNewEmail'>(CONST.nav.profileNewEmail, {
            state: { ...location.state, token: data.token },
          });
        } else if (data.next === VerifyNextEnum.sendOldEmail) {
          navigate<'sendOldEmail'>(CONST.nav.profileOldEmail, {
            state: { ...location.state },
          });
        }
      })
      .catch(err => err);
  };

  const onBack = () => navigate(CONST.nav.profileOldEmail);

  return (
    <LoginLayout className={cn(null, [className])} data-testid={ti()} backButton={onBack}>
      <form className={cn('content')} onSubmit={onSubmit}>
        <h1 className={cn('title')}>{t('public.password')}</h1>
        <Input
          className={cn('input')}
          isLoading={isLoading}
          type="password"
          placeholder={t('public.password_placeholder')}
          name="password"
          value={password}
          onChange={handleChangePassword}
          autoComplete="off"
          error={error}
          autoFocus
        />

        <Button
          className={cn('button')}
          type="submit"
          disabled={!password || isLoading}
          isLoading={isLoading}
          data-testid={ti('submit')}
        >
          {t('public.go_next')}
        </Button>
      </form>
    </LoginLayout>
  );
};

export default VerifyPassword;
